import { useAppTranslation } from "@/utils";
import {
  Button,
  Card,
  Flex,
  Input,
  InputNumber,
  LegacyForm,
  Radio,
  Select,
  Typography,
  useMessagedAsyncFn,
  useMessagedAsyncRetry,
} from "@payping/web-components";
import { ValidCardNumber, ValidEmail, ValidPostalCode } from "@payping/utils/validation";
import { moment } from "@payping/utils/dateUtils";
import { bnplConsumerApis } from "@payping/api";
import type { FillProfileRequest, SDKRequestInit } from "@payping/api";
import Loading from "@/pages/loading";
import provinces from "@/utils/provinces.json";
import cities from "@/utils/cities.json";
import { useEffect, useState } from "react";
import { clsx } from "clsx";
import { isPersianText } from "@payping/utilities-shared/validations";
import { normalizePersian } from "@payping/utilities-shared/string";
import PayUnAvailable from "../../Unavailable";
import PayGetOrderLayout from "../Layout";

interface FormInputsOne {
  firstName: string;
  lastName: string;
  idCardSerialNumber: string;
  postalCode: number;
  email: string;
  cardNumber: number;
  sex: "0" | "1";
}
interface FormInputsTwo {
  province: string;
  city: string;
  address: string;
}

interface FormInputsThree {
  firstNameFa: string;
  lastnameFa: string;
  fatherNameFa: string;
}

interface ProfileProps {
  trackingCode: string;
  afterSuccess: () => void;
}

const PayGetOrderProfile = ({ afterSuccess, trackingCode }: ProfileProps) => {
  const { t } = useAppTranslation();
  const [formOne] = LegacyForm.useForm<FormInputsOne>();
  const [formTwo] = LegacyForm.useForm<FormInputsTwo>();
  const [formThree] = LegacyForm.useForm<FormInputsThree>();
  const [addressIsRequired, setAddressIsRequired] = useState(false);
  const [extraInfoRequired, setExtraInfoRequired] = useState(false);

  const provinceWatch = LegacyForm.useWatch("province", formTwo);
  const provinceCitiesList = provinceWatch ? (cities as any)[provinceWatch] : undefined;

  const sexOptions = [
    { label: t("common.male"), value: "0" },
    { label: t("common.female"), value: "1" },
  ];

  const {
    loading,
    error,
    value: profileValue,
    retry,
  } = useMessagedAsyncRetry(
    async () => {
      const res = await bnplConsumerApis.getProfile({ isRaw: true } as SDKRequestInit);
      const calcSex = () => {
        switch (res.isMale) {
          case true:
            return "0";
          case false:
            return "1";
          default:
            return "0";
        }
      };
      formTwo.setFieldValue("address", res.address ?? "");
      formTwo.setFieldValue("city", res.cityEnglish ?? undefined);
      formOne.setFieldValue("email", res.email ?? "");
      formOne.setFieldValue("firstName", res.firstNameEnglish ?? "");
      formOne.setFieldValue("cardNumber", (res.cardNumber ?? "").replace("IR", ""));
      formOne.setFieldValue("lastName", res.lastNameEnglish ?? "");
      formOne.setFieldValue("idCardSerialNumber", res.nationalIdSeries ?? "");
      formOne.setFieldValue("postalCode", res.postalCode ?? "");
      formTwo.setFieldValue("province", res.provinceEnglish ?? undefined);
      formOne.setFieldValue("sex", calcSex());

      return res;
    },
    [formOne, formTwo],
    {
      success: false,
      error: true,
    },
  );

  const [saveProfileState, saveProfile] = useMessagedAsyncFn(
    async (body: FillProfileRequest) => {
      try {
        const res = await bnplConsumerApis.fillProfile(body, { isRaw: true } as SDKRequestInit);
        afterSuccess();
        return res;
      } catch (e: any) {
        if (e.status === 400) {
          try {
            const parsedError = await e.json();
            throw parsedError;
          } catch (jsonError: any) {
            if (jsonError.status === 400) {
              // address required
              if ((jsonError as any)?.key === 111) {
                setAddressIsRequired(true);

                return undefined;
              }
              // sabt ahval javab nadade
              if ((jsonError as any)?.key === 120) {
                setExtraInfoRequired(true);
                return undefined;
              }
              const errors = jsonError?.errors;
              const messages = (
                errors instanceof Array
                  ? errors.map((item: any) => (typeof item?.message === "string" ? item?.message : ""))
                  : []
              ).filter((item: string) => item !== "");
              throw new Error(messages.length ? JSON.stringify(messages.join(" , ")) : t("common.serverError"), {
                cause: jsonError,
              });
            }
            throw e; // Fallback if parsing JSON fails
          }
        }
        // Handle any other errors
        throw e;
      }
    },
    [afterSuccess, t],
    { loading: false },
    { error: true },
  );

  useEffect(() => {
    const currentCity = formTwo.getFieldValue("city");
    if (!provinceCitiesList) {
      return;
    }
    if (!provinceCitiesList.children.find((i: any) => i.CityNameEn === currentCity)) {
      formTwo.setFieldValue("city", undefined);
    }
  }, [formTwo, provinceCitiesList, provinceWatch]);

  if (loading || error?.name === "AuthError") {
    return (
      <PayGetOrderLayout showHeaders={false}>
        <Loading />
      </PayGetOrderLayout>
    );
  }

  if (error) {
    return (
      <PayGetOrderLayout showHeaders={false}>
        <PayUnAvailable
          onRetry={() => {
            retry();
          }}
          onRetryLoading={loading}
        />
      </PayGetOrderLayout>
    );
  }

  const handleSubmitFormOne = (data: FormInputsOne) => {
    saveProfile({
      email: data.email,
      firstNameEnglish: data.firstName,
      cardNumber: `${data.cardNumber}`,
      lastNameEnglish: data.lastName,
      nationalIdSeries: data.idCardSerialNumber,
      postalCode: data.postalCode.toString(),
      trackingCode,
      isMale: String(data.sex) === "0",
    });
  };

  const handleSubmitFormTwo = (data: FormInputsTwo) => {
    saveProfile({
      address: data.address,
      cityEnglish: data.city,
      email: formOne.getFieldValue("email"),
      firstNameEnglish: formOne.getFieldValue("firstName"),
      cardNumber: `${formOne.getFieldValue("cardNumber")}`,
      lastNameEnglish: formOne.getFieldValue("lastName"),
      nationalIdSeries: formOne.getFieldValue("idCardSerialNumber"),
      postalCode: `${formOne.getFieldValue("postalCode")}`,
      provinceEnglish: data.province,
      trackingCode,
      isMale: String(formOne.getFieldValue("sex")) === "0",
      ...(extraInfoRequired
        ? {
            firstNameFa: formThree.getFieldValue("firstNameFa") ?? profileValue?.firstName,
            lastnameFa: formThree.getFieldValue("lastnameFa") ?? profileValue?.lastName,
            fatherNameFa: formThree.getFieldValue("fatherNameFa") ?? profileValue?.fatherName,
          }
        : {}),
    });
  };

  const handleSubmitFormThree = (data: FormInputsThree) => {
    saveProfile({
      cityEnglish: formTwo.getFieldValue("city") ?? profileValue?.cityEnglish,
      email: formOne.getFieldValue("email"),
      firstNameEnglish: formOne.getFieldValue("firstName"),
      cardNumber: `${formOne.getFieldValue("cardNumber")}`,
      lastNameEnglish: formOne.getFieldValue("lastName"),
      nationalIdSeries: formOne.getFieldValue("idCardSerialNumber"),
      postalCode: `${formOne.getFieldValue("postalCode")}`,
      provinceEnglish: formTwo.getFieldValue("province") ?? profileValue?.provinceEnglish,
      firstNameFa: data.firstNameFa,
      lastnameFa: data.lastnameFa,
      fatherNameFa: data.fatherNameFa,
      trackingCode,
      isMale: String(formOne.getFieldValue("sex")) === "0",
      ...(addressIsRequired ? { address: formTwo.getFieldValue("address") ?? profileValue?.address } : {}),
    });
  };

  return (
    <PayGetOrderLayout showHeaders>
      <Flex vertical className="tw:w-full tw:px-4! tw:pb-4! tw:pt-6!" justify="space-between">
        <Flex vertical className="tw:w-full" gap={"middle"}>
          <Flex vertical className="tw:w-full" gap={"small"}>
            <Typography.Title className="tw:m-0!" level={5}>
              {t("pay.getOrder.steps.fillProfile.title")}
            </Typography.Title>
            <Typography.Text>{t("pay.getOrder.steps.fillProfile.content")}</Typography.Text>
          </Flex>
          <Card size="small">
            <Flex vertical className="tw:w-full" gap={"middle"}>
              <Flex justify="space-between" className="tw:w-full">
                <Typography.Text type="secondary">
                  {t("pay.getOrder.steps.fillProfile.fillForm.firstName")}
                </Typography.Text>
                <Typography.Text>{profileValue?.firstName || ""}</Typography.Text>
              </Flex>
              <Flex justify="space-between" className="tw:w-full">
                <Typography.Text type="secondary">
                  {t("pay.getOrder.steps.fillProfile.fillForm.lastName")}
                </Typography.Text>
                <Typography.Text>{profileValue?.lastName || ""}</Typography.Text>
              </Flex>
              <Flex justify="space-between" className="tw:w-full">
                <Typography.Text type="secondary">
                  {t("pay.getOrder.steps.fillProfile.fillForm.dateOfBirth")}
                </Typography.Text>
                <Typography.Text>
                  {profileValue?.birthDate ? moment(profileValue?.birthDate).format("jYYYY/jMM/jDD") : ""}
                </Typography.Text>
              </Flex>
              <Flex justify="space-between" className="tw:w-full">
                <Typography.Text type="secondary">
                  {t("pay.getOrder.steps.fillProfile.fillForm.nationalCode")}
                </Typography.Text>
                <Typography.Text>{profileValue?.nationalCode || ""}</Typography.Text>
              </Flex>
            </Flex>
          </Card>

          <LegacyForm
            form={formOne}
            className={clsx(["tw:mt-4!", { "tw:hidden": addressIsRequired || extraInfoRequired }])}
            layout="vertical"
            onFinish={handleSubmitFormOne}
          >
            <LegacyForm.Item
              rules={[{ required: true }, { pattern: /^[A-Za-z\s]+$/, message: t("formValidate.onlyEnglish") }]}
              name={"firstName"}
              label={t("pay.getOrder.steps.fillProfile.fillForm.latinFirstName")}
            >
              <Input />
            </LegacyForm.Item>
            <LegacyForm.Item
              rules={[{ required: true }, { pattern: /^[A-Za-z\s]+$/, message: t("formValidate.onlyEnglishLetters") }]}
              name={"lastName"}
              label={t("pay.getOrder.steps.fillProfile.fillForm.latinLastName")}
            >
              <Input />
            </LegacyForm.Item>
            <LegacyForm.Item label={t("common.sex")} name="sex" rules={[{ required: true }]}>
              <Radio.Group options={sexOptions} />
            </LegacyForm.Item>
            {/* <LegacyForm.Item
              rules={[{ required: true }]}
              name={"birthCertificateNumber"}
              label={t("pay.getOrder.steps.fillProfile.fillForm.birthCertificateNumber")}
            >
              <Input.Number inputMode="numeric" changeOnWheel={false} controls={false} className="tw:w-full" />
            </LegacyForm.Item> */}
            <LegacyForm.Item
              rules={[
                { required: true },
                // { pattern: /^[A-Za-z0-9]+$/, message: t("formValidate.onlyEnglish") },
                // { min: 8, message: t("formValidate.min", { min: 8 }) },
                // { max: 10, message: t("formValidate.max", { max: 10 }) },
              ]}
              name={"idCardSerialNumber"}
              label={t("common.nationalIdSeriesOrTrackingCode")}
            >
              <Input />
            </LegacyForm.Item>

            <LegacyForm.Item
              rules={[
                { required: true },
                {
                  validator(_, emailValue) {
                    if (ValidPostalCode(emailValue)) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error(
                        t("formValidate.invalid", { name: t("pay.getOrder.steps.fillProfile.fillForm.postalCode") }),
                      ),
                    );
                  },
                },
              ]}
              name={"postalCode"}
              label={t("pay.getOrder.steps.fillProfile.fillForm.postalCode")}
            >
              <InputNumber className="tw:w-full!" inputMode="numeric" changeOnWheel={false} controls={false} />
            </LegacyForm.Item>
            <LegacyForm.Item
              rules={[
                { required: true },
                {
                  validator(_, emailValue) {
                    if (ValidEmail(emailValue)) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error(t("formValidate.invalidEmail")));
                  },
                },
              ]}
              name={"email"}
              label={t("pay.getOrder.steps.fillProfile.fillForm.email")}
            >
              <Input />
            </LegacyForm.Item>
            <LegacyForm.Item
              rules={[
                { required: true },
                {
                  validator(_, cardNumber) {
                    if (String(cardNumber).length !== 16) {
                      return Promise.reject(new Error(t("formValidate.cardNumberLength", { count: 16 })));
                    }
                    if (!ValidCardNumber(cardNumber.toString())) {
                      return Promise.reject(new Error(t("formValidate.invalidCardNumber")));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              name={"cardNumber"}
              label={t("pay.getOrder.steps.fillProfile.fillForm.cardNumber")}
            >
              <InputNumber
                inputMode="numeric"
                changeOnWheel={false}
                controls={false}
                className="tw:w-full!"
                placeholder={t("pay.getOrder.steps.fillProfile.fillForm.cardNumberPlaceholder")}
              />
            </LegacyForm.Item>
            <Button className="tw:mt-4!" htmlType="submit" type="primary" block loading={saveProfileState.loading}>
              {t("common.confirmAndContinue")}
            </Button>
          </LegacyForm>

          <LegacyForm
            form={formTwo}
            className={clsx(["tw:mt-4!", { "tw:hidden": !addressIsRequired }])}
            layout="vertical"
            onFinish={handleSubmitFormTwo}
          >
            <LegacyForm.Item
              rules={[{ required: true }]}
              name={"province"}
              label={t("pay.getOrder.steps.fillProfile.fillForm.province")}
              tooltip={t("pay.getOrder.steps.fillProfile.fillForm.chooseAnOption")}
            >
              <Select>
                {provinces.map((item) => (
                  <Select.Option key={item.ProvinceAbbreviation} value={item.ProvinceAbbreviation}>
                    {item.ProvinceNameFa}
                  </Select.Option>
                ))}
              </Select>
            </LegacyForm.Item>

            <LegacyForm.Item
              rules={[{ required: true }]}
              name={"city"}
              label={t("pay.getOrder.steps.fillProfile.fillForm.city")}
              tooltip={t("pay.getOrder.steps.fillProfile.fillForm.chooseAnOption")}
            >
              <Select disabled={!provinceWatch}>
                {provinceCitiesList
                  ? provinceCitiesList?.children.map((item: any) => (
                      <Select.Option key={item.CityNameEn} value={item.CityNameEn}>
                        {item.CityNameFa}
                      </Select.Option>
                    ))
                  : null}
              </Select>
            </LegacyForm.Item>

            <LegacyForm.Item
              rules={[{ required: true }, { max: 180, message: t("formValidate.max", { max: 180 }) }]}
              name={"address"}
              label={t("pay.getOrder.steps.fillProfile.fillForm.address")}
            >
              <Input.TextArea
                maxLength={180}
                placeholder={t("pay.getOrder.steps.fillProfile.fillForm.addressPlaceholder")}
              />
            </LegacyForm.Item>
            <Button className="tw:mt-4!" htmlType="submit" type="primary" block loading={saveProfileState.loading}>
              {t("common.confirmAndContinue")}
            </Button>
          </LegacyForm>
          <LegacyForm
            form={formThree}
            className={clsx(["tw:mt-4!", { "tw:hidden": !extraInfoRequired }])}
            layout="vertical"
            onFinish={handleSubmitFormThree}
          >
            <LegacyForm.Item
              rules={[
                {
                  validator(_, value) {
                    if (!value || String(value).length < 3) {
                      return Promise.reject(new Error(t("formValidate.min", { min: 3 })));
                    }
                    // not valid
                    if (!isPersianText(String(value))) {
                      return Promise.reject(new Error(t("formValidate.invalidPersian")));
                    }

                    return Promise.resolve();
                  },
                },
              ]}
              name={"firstNameFa"}
              label={t("pay.getOrder.steps.fillProfile.fillForm.firstNameFa")}
              normalize={(value) => normalizePersian({ text: value })}
            >
              <Input />
            </LegacyForm.Item>

            <LegacyForm.Item
              rules={[
                {
                  validator(_, value) {
                    if (!value || String(value).length < 3) {
                      return Promise.reject(new Error(t("formValidate.min", { min: 3 })));
                    }
                    // not valid
                    if (!isPersianText(String(value))) {
                      return Promise.reject(new Error(t("formValidate.invalidPersian")));
                    }

                    return Promise.resolve();
                  },
                },
              ]}
              name={"lastnameFa"}
              label={t("pay.getOrder.steps.fillProfile.fillForm.lastnameFa")}
              normalize={(value) => normalizePersian({ text: value })}
            >
              <Input />
            </LegacyForm.Item>

            <LegacyForm.Item
              rules={[
                {
                  validator(_, value) {
                    if (!value || String(value).length < 3) {
                      return Promise.reject(new Error(t("formValidate.min", { min: 3 })));
                    }
                    // not valid
                    if (!isPersianText(String(value))) {
                      return Promise.reject(new Error(t("formValidate.invalidPersian")));
                    }

                    return Promise.resolve();
                  },
                },
              ]}
              name={"fatherNameFa"}
              label={t("pay.getOrder.steps.fillProfile.fillForm.fatherNameFa")}
              normalize={(value) => normalizePersian({ text: value })}
            >
              <Input />
            </LegacyForm.Item>

            <Button className="tw:mt-4!" htmlType="submit" type="primary" block loading={saveProfileState.loading}>
              {t("common.confirmAndContinue")}
            </Button>
          </LegacyForm>
        </Flex>
      </Flex>
    </PayGetOrderLayout>
  );
};

export default PayGetOrderProfile;
